import { PostWithDetails, Tournament } from '@/src/domain/models';
import type { SocialCounts } from '@/src/domain/models';
import type { UserTrainingStats } from '@/src/domain/models/TrainingLaunch';
import type { UserStats } from '@/src/domain/usecases/GetUserStatsUseCase';
import { useTranslation } from '@/src/i18n';
import { PatternBackground, PostCard, SocialStatsBar, TrainingStatsCard } from '@/src/presentation/components';
import { FontAwesome5, Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { useState } from 'react';
import { ActivityIndicator, FlatList, Image, ScrollView, Text, TouchableOpacity, View } from 'react-native';
import { createStyles } from './ProfileScreen.styles';

export interface ProfileScreenProps {
  userName: string;
  userEmail: string;
  userBio: string;
  userProfile: any;
  stats: UserStats;
  tournaments: Tournament[];
  posts: PostWithDetails[];
  loadingPosts: boolean;
  loadingMorePosts: boolean;
  hasMorePosts: boolean;
  isOwnProfile: boolean;
  socialCounts?: SocialCounts;
  isFollowing?: boolean;
  friendshipStatus?: 'none' | 'pending_sent' | 'pending_received' | 'friends';
  handleTournamentPress: (tournamentId: string, isPublic?: boolean) => void;
  handleEditProfile: () => void;
  handleFollowToggle?: () => void;
  handleFriendAction?: () => void;
  handleChallenge?: () => void;
  handleFollowersPress?: () => void;
  handleFollowingPress?: () => void;
  handleFriendsPress?: () => void;
  handleUserPress: (userId: string) => void;
  handlePostTournamentPress?: (tournamentId: string) => void;
  handleDeletePost?: (postId: string) => void;
  handleLikePress?: (postId: string, isLiked: boolean) => void;
  handleSharePost?: (postId: string) => void;
  handleLoadMorePosts: () => void;
  isUserPro?: boolean;
  trainingStats?: UserTrainingStats | null;
  colors: {
    background: string;
    surface: string;
    card: string;
    border: string;
    text: string;
    textSecondary: string;
    textTertiary: string;
    primary: string;
    primaryText: string;
    badgeSetup: string;
    badgeProgress: string;
    badgeCompleted: string;
    error: string;
    divider: string;
  };
}

type TabType = 'tournaments' | 'stats' | 'posts';

export const ProfileScreen = ({
  userName,
  userEmail,
  userBio,
  userProfile,
  stats,
  tournaments,
  posts,
  loadingPosts,
  loadingMorePosts,
  hasMorePosts,
  isOwnProfile,
  socialCounts,
  isFollowing,
  friendshipStatus,
  handleTournamentPress,
  handleEditProfile,
  handleFollowToggle,
  handleFriendAction,
  handleChallenge,
  handleFollowersPress,
  handleFollowingPress,
  handleFriendsPress,
  handleUserPress,
  handlePostTournamentPress,
  handleDeletePost,
  handleLikePress,
  handleSharePost,
  handleLoadMorePosts,
  isUserPro,
  trainingStats,
  colors,
}: ProfileScreenProps) => {
  const styles = createStyles();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabType>('tournaments');

  const getFriendButtonText = () => {
    switch (friendshipStatus) {
      case 'friends':
        return t('social.friends');
      case 'pending_sent':
        return t('profile.requestSent');
      case 'pending_received':
        return t('profile.acceptRequest');
      default:
        return t('social.addFriend');
    }
  };

  const getFriendButtonIcon = () => {
    switch (friendshipStatus) {
      case 'friends':
        return 'people';
      case 'pending_sent':
        return 'time-outline';
      case 'pending_received':
        return 'person-add';
      default:
        return 'person-add-outline';
    }
  };

  return (
    <PatternBackground>
    <ScrollView style={styles.container} contentContainerStyle={styles.scrollContent}>
      {/* Header */}
      <View style={[styles.header, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
        {/* Top row: avatar + name/email/bio */}
        <View style={styles.headerTop}>
          {userProfile?.raw_user_meta_data?.avatar_url ? (
            <Image source={{ uri: userProfile.raw_user_meta_data.avatar_url }} style={styles.avatarImage} />
          ) : (
            <View style={[styles.avatar, { backgroundColor: colors.primary }]}>
              <Text style={styles.avatarText}>{userName.charAt(0).toUpperCase()}</Text>
            </View>
          )}
          <View style={styles.headerInfo}>
            <View style={styles.nameRow}>
              <Text style={[styles.userName, { color: colors.text }]}>{userName}</Text>
              {isUserPro && (
                <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: '#FFD700', paddingHorizontal: 7, paddingVertical: 2, borderRadius: 8 }}>
                  <Ionicons name="star" size={11} color="#8B4513" />
                  <Text style={{ fontSize: 10, fontWeight: 'bold', color: '#8B4513', marginLeft: 2 }}>PRO</Text>
                </View>
              )}
            </View>
            <Text style={[styles.userEmail, { color: colors.textSecondary }]}>{userEmail}</Text>
            {userBio && <Text style={[styles.userBio, { color: colors.textSecondary }]}>{userBio}</Text>}
          </View>
        </View>

        {/* Champion badges — compact chip row */}
        {stats && (stats.tournamentsWon > 0 || stats.challengesWon > 0) && (
          <View style={styles.badgesRow}>
            {stats.tournamentsWon > 0 && (
              <View style={[styles.championBadge, { backgroundColor: '#FFD700' }]}>
                <FontAwesome5 name="trophy" size={12} color="#8B4513" />
                <Text style={styles.championBadgeText}>
                  {stats.tournamentsWon} {stats.tournamentsWon === 1 ? t('profile.championship') : t('profile.championships')}
                </Text>
              </View>
            )}
            {stats.challengesWon > 0 && (
              <View style={[styles.championBadge, { backgroundColor: '#E8B4B4' }]}>
                <MaterialCommunityIcons name="sword-cross" size={12} color="#8B4513" />
                <Text style={styles.championBadgeText}>
                  {stats.challengesWon} {t('profile.challengeWins')}
                </Text>
              </View>
            )}
          </View>
        )}

        {/* Social Stats Bar */}
        {socialCounts && handleFollowersPress && handleFollowingPress && handleFriendsPress && (
          <SocialStatsBar
            followersCount={socialCounts.followersCount}
            followingCount={socialCounts.followingCount}
            friendsCount={socialCounts.friendsCount}
            onFollowersPress={handleFollowersPress}
            onFollowingPress={handleFollowingPress}
            onFriendsPress={handleFriendsPress}
            colors={colors}
          />
        )}

        {/* Action Buttons */}
        {isOwnProfile ? (
          <TouchableOpacity
            style={[styles.editProfileBtn, { backgroundColor: colors.primary }]}
            onPress={handleEditProfile}
          >
            <FontAwesome5 name="edit" size={14} color={colors.primaryText} />
            <Text style={[styles.editProfileBtnText, { color: colors.primaryText }]}>{t('profile.editProfile')}</Text>
          </TouchableOpacity>
        ) : (
          <View style={styles.socialActions}>
            {handleFollowToggle && (
              <TouchableOpacity
                style={[
                  styles.socialButton,
                  isFollowing
                    ? { backgroundColor: colors.card, borderColor: colors.border, borderWidth: 1 }
                    : { backgroundColor: colors.primary },
                ]}
                onPress={handleFollowToggle}
              >
                <Ionicons
                  name={isFollowing ? 'person-remove-outline' : 'person-add-outline'}
                  size={16}
                  color={isFollowing ? colors.text : colors.primaryText}
                />
                <Text style={[styles.socialButtonText, { color: isFollowing ? colors.text : colors.primaryText }]}>
                  {isFollowing ? t('social.unfollow') : t('social.follow')}
                </Text>
              </TouchableOpacity>
            )}

            {handleFriendAction && (
              <TouchableOpacity
                style={[
                  styles.socialButton,
                  friendshipStatus === 'friends' || friendshipStatus === 'pending_sent'
                    ? { backgroundColor: colors.card, borderColor: colors.border, borderWidth: 1 }
                    : { backgroundColor: colors.primary },
                ]}
                onPress={handleFriendAction}
              >
                <Ionicons
                  name={getFriendButtonIcon()}
                  size={16}
                  color={friendshipStatus === 'friends' || friendshipStatus === 'pending_sent' ? colors.text : colors.primaryText}
                />
                <Text
                  style={[
                    styles.socialButtonText,
                    { color: friendshipStatus === 'friends' || friendshipStatus === 'pending_sent' ? colors.text : colors.primaryText },
                  ]}
                >
                  {getFriendButtonText()}
                </Text>
              </TouchableOpacity>
            )}

            {handleChallenge && (
              <TouchableOpacity
                style={[styles.socialButton, { backgroundColor: '#FFD700', borderColor: '#FFC107', borderWidth: 1 }]}
                onPress={handleChallenge}
              >
                <MaterialCommunityIcons name="sword-cross" size={16} color="#8B4513" />
                <Text style={[styles.socialButtonText, { color: '#8B4513' }]}>{t('social.challenge')}</Text>
              </TouchableOpacity>
            )}
          </View>
        )}
      </View>

      {/* Quick Stats */}
      <View style={[styles.statsSection, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
        <View style={styles.statCard}>
          <Text style={[styles.statValue, { color: colors.primary }]}>{stats?.tournamentsPlayed || 0}</Text>
          <Text style={[styles.statLabel, { color: colors.textSecondary }]}>{t('tournaments.tournaments')}</Text>
        </View>
        <View style={styles.statCard}>
          <Text style={[styles.statValue, { color: '#FFD700' }]}>{stats?.tournamentsWon || 0}</Text>
          <Text style={[styles.statLabel, { color: colors.textSecondary }]}>{t('profile.won')}</Text>
        </View>
        <View style={styles.statCard}>
          <Text style={[styles.statValue, { color: '#E57373' }]}>{stats?.challengesWon || 0}</Text>
          <Text style={[styles.statLabel, { color: colors.textSecondary }]}>{t('social.challenges')}</Text>
        </View>
      </View>

      {/* Tabs */}
      <View style={[styles.tabsContainer, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
        <TouchableOpacity
          style={[styles.tab, activeTab === 'tournaments' && styles.activeTab]}
          onPress={() => setActiveTab('tournaments')}
        >
          <MaterialCommunityIcons
            name="tournament"
            size={20}
            color={activeTab === 'tournaments' ? colors.primary : colors.textSecondary}
          />
          <Text
            style={[
              styles.tabText,
              {
                color: activeTab === 'tournaments' ? colors.primary : colors.textSecondary,
                fontWeight: activeTab === 'tournaments' ? '600' : '400',
              },
            ]}
          >
            {t('tournaments.tournaments')}
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.tab, activeTab === 'stats' && styles.activeTab]}
          onPress={() => setActiveTab('stats')}
        >
          <Ionicons
            name="stats-chart"
            size={20}
            color={activeTab === 'stats' ? colors.primary : colors.textSecondary}
          />
          <Text
            style={[
              styles.tabText,
              {
                color: activeTab === 'stats' ? colors.primary : colors.textSecondary,
                fontWeight: activeTab === 'stats' ? '600' : '400',
              },
            ]}
          >
            {t('profile.stats')}
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.tab, activeTab === 'posts' && styles.activeTab]}
          onPress={() => setActiveTab('posts')}
        >
          <Ionicons
            name="newspaper-outline"
            size={20}
            color={activeTab === 'posts' ? colors.primary : colors.textSecondary}
          />
          <Text
            style={[
              styles.tabText,
              {
                color: activeTab === 'posts' ? colors.primary : colors.textSecondary,
                fontWeight: activeTab === 'posts' ? '600' : '400',
              },
            ]}
          >
            {t('profile.posts')}
          </Text>
        </TouchableOpacity>
      </View>

      {/* Tab Content */}
      {activeTab === 'tournaments' && (
        <View style={styles.historySection}>
        <Text style={[styles.sectionTitle, { color: colors.text }]}>{t('profile.tournamentHistory')} ({tournaments.length})</Text>

        {tournaments.length === 0 ? (
          <View style={styles.emptyContainer}>
            <Text style={[styles.emptyText, { color: colors.textSecondary }]}>{t('profile.noTournamentsYet')}</Text>
          </View>
        ) : (
          tournaments.map((item) => {
            return (
              <TouchableOpacity
                key={item.id}
                style={[styles.tournamentItem, { backgroundColor: colors.card, borderColor: colors.border }]}
                onPress={() => handleTournamentPress(item.id, item.isPublic)}
                activeOpacity={0.7}
              >
                <View style={styles.tournamentHeader}>
                  <View style={styles.tournamentNameRow}>
                    <Text style={[styles.tournamentName, { color: colors.text }]}>{item.name}</Text>
                    {item.status === 'completed' && (
                      <FontAwesome5 name="trophy" size={16} color="#FFD700" style={{ marginLeft: 6 }} />
                    )}
                  </View>
                  <View
                    style={[
                      styles.statusBadge,
                      {
                        backgroundColor:
                          item.status === 'completed'
                            ? colors.badgeCompleted
                            : item.status === 'in_progress'
                              ? colors.badgeProgress
                              : colors.badgeSetup,
                      },
                    ]}
                  >
                    <Text style={[styles.statusText, { color: colors.text }]}>{t(`status.tournamentStatus.${item.status}`)}</Text>
                  </View>
                </View>
                <View style={styles.tournamentInfo}>
                  <View style={styles.infoRow}>
                    {item.tournamentType === 'single_elimination' && (
                      <>
                        <FontAwesome5 name="trophy" size={12} color={colors.textSecondary} />
                        <Text style={[styles.tournamentType, { color: colors.textSecondary }]}>{t('tournaments.singleElimination')}</Text>
                      </>
                    )}
                    {item.tournamentType === 'double_elimination' && (
                      <>
                        <MaterialCommunityIcons name="tournament" size={14} color={colors.textSecondary} />
                        <Text style={[styles.tournamentType, { color: colors.textSecondary }]}>{t('tournaments.doubleElimination')}</Text>
                      </>
                    )}
                    {item.tournamentType === 'round_robin' && (
                      <>
                        <MaterialCommunityIcons name="sync-circle" size={14} color={colors.textSecondary} />
                        <Text style={[styles.tournamentType, { color: colors.textSecondary }]}>{t('tournaments.roundRobin')}</Text>
                      </>
                    )}
                    {item.tournamentType === 'swiss' && (
                      <>
                        <MaterialCommunityIcons name="grid" size={14} color={colors.textSecondary} />
                        <Text style={[styles.tournamentType, { color: colors.textSecondary }]}>{t('tournaments.swiss')}</Text>
                      </>
                    )}
                  </View>
                  <View style={styles.infoRow}>
                    <Ionicons name="calendar-outline" size={12} color={colors.textTertiary} />
                    <Text style={[styles.tournamentDate, { color: colors.textTertiary }]}>
                      {new Date(item.createdAt).toLocaleDateString()}
                    </Text>
                  </View>
                </View>
              </TouchableOpacity>
            );
          })
        )}
        </View>
      )}

      {/* Stats Tab */}
      {activeTab === 'stats' && (
        <View style={styles.historySection}>
          <Text style={[styles.sectionTitle, { color: colors.text }]}>{t('profile.detailedStatistics')}</Text>

          <View style={[styles.detailedStatsCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
            <View style={styles.detailedStatRow}>
              <Text style={[styles.detailedStatLabel, { color: colors.textSecondary }]}>{t('profile.tournamentsPlayed')}</Text>
              <Text style={[styles.detailedStatValue, { color: colors.text }]}>{stats?.tournamentsPlayed || 0}</Text>
            </View>
            <View style={[styles.statDivider, { backgroundColor: colors.border }]} />

            <View style={styles.detailedStatRow}>
              <Text style={[styles.detailedStatLabel, { color: colors.textSecondary }]}>{t('profile.tournamentsWon')}</Text>
              <Text style={[styles.detailedStatValue, { color: '#FFD700' }]}>{stats?.tournamentsWon || 0}</Text>
            </View>
            <View style={[styles.statDivider, { backgroundColor: colors.border }]} />

            <View style={styles.detailedStatRow}>
              <Text style={[styles.detailedStatLabel, { color: colors.textSecondary }]}>{t('profile.challengesPlayed')}</Text>
              <Text style={[styles.detailedStatValue, { color: colors.text }]}>{stats?.challengesPlayed || 0}</Text>
            </View>
            <View style={[styles.statDivider, { backgroundColor: colors.border }]} />

            <View style={styles.detailedStatRow}>
              <Text style={[styles.detailedStatLabel, { color: colors.textSecondary }]}>{t('profile.challengesWon')}</Text>
              <Text style={[styles.detailedStatValue, { color: '#E57373' }]}>{stats?.challengesWon || 0}</Text>
            </View>
            <View style={[styles.statDivider, { backgroundColor: colors.border }]} />

            <View style={styles.detailedStatRow}>
              <Text style={[styles.detailedStatLabel, { color: colors.textSecondary }]}>{t('profile.totalWins')}</Text>
              <Text style={[styles.detailedStatValue, { color: '#4CAF50' }]}>{stats?.wins || 0}</Text>
            </View>
            <View style={[styles.statDivider, { backgroundColor: colors.border }]} />

            <View style={styles.detailedStatRow}>
              <Text style={[styles.detailedStatLabel, { color: colors.textSecondary }]}>{t('profile.totalLosses')}</Text>
              <Text style={[styles.detailedStatValue, { color: '#F44336' }]}>{stats?.losses || 0}</Text>
            </View>
            <View style={[styles.statDivider, { backgroundColor: colors.border }]} />

            <View style={styles.detailedStatRow}>
              <Text style={[styles.detailedStatLabel, { color: colors.textSecondary }]}>{t('profile.winRate')}</Text>
              <Text style={[styles.detailedStatValue, { color: colors.primary }]}>
                {stats?.wins && stats?.losses
                  ? `${Math.round((stats.wins / (stats.wins + stats.losses)) * 100)}%`
                  : '0%'}
              </Text>
            </View>
          </View>

          {trainingStats && trainingStats.totalLaunches > 0 && (
            <TrainingStatsCard stats={trainingStats} colors={colors} />
          )}
        </View>
      )}

      {/* Posts Tab */}
      {activeTab === 'posts' && (
        <View style={styles.postsSection}>
          {loadingPosts ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" color={colors.primary} />
            </View>
          ) : posts.length === 0 ? (
            <View style={styles.emptyContainer}>
              <Ionicons name="newspaper-outline" size={48} color={colors.textSecondary} />
              <Text style={[styles.emptyText, { color: colors.textSecondary }]}>{t('profile.noPostsYet')}</Text>
            </View>
          ) : (
            <FlatList
              data={posts}
              renderItem={({ item }) => (
                <PostCard
                  post={item}
                  currentUserId={isOwnProfile ? item.userId : undefined}
                  onUserPress={handleUserPress}
                  onTournamentPress={handlePostTournamentPress}
                  onDeletePress={isOwnProfile ? handleDeletePost : undefined}
                  onLikePress={handleLikePress}
                  onSharePress={handleSharePost}
                  colors={colors}
                />
              )}
              keyExtractor={(item) => item.id}
              contentContainerStyle={styles.postsList}
              onEndReached={handleLoadMorePosts}
              onEndReachedThreshold={0.5}
              scrollEnabled={false}
              ListFooterComponent={
                loadingMorePosts && hasMorePosts ? (
                  <View style={styles.loadingMoreContainer}>
                    <ActivityIndicator size="small" color={colors.primary} />
                  </View>
                ) : null
              }
            />
          )}
        </View>
      )}
    </ScrollView>
    </PatternBackground>
  );
};

export const ProfileScreenLoading = ({ colors }: { colors: { background: string; primary: string } }) => {
  const styles = createStyles();
  return (
    <PatternBackground>
      <View style={styles.centerContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    </PatternBackground>
  );
};

export const ProfileScreenError = ({ colors, message }: { colors: { background: string; error: string }; message: string }) => {
  const styles = createStyles();
  return (
    <PatternBackground>
      <View style={styles.centerContainer}>
        <Text style={[styles.errorText, { color: colors.error }]}>{message}</Text>
      </View>
    </PatternBackground>
  );
};
